import { cloneDeep } from 'lodash-es';
import { BotItem } from '@/models/bot';
import { createBotsPageHeaders } from '@/pages/admin/bots/BotsPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { botService } from '@/services/botService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { IPageOptions } from '@/types';
import { IBotFilters } from '@/types/bot';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'name', order: 'asc' as const }];

function createBotStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<BotItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const filters = reactive<IBotFilters>({
        botStatus: {
            value: null,
            title: 'bots.filters.status',
            allowSaveState: true,
        },
        botName: {
            value: null,
            title: 'bots.filters.name',
            allowSaveState: true,
        },
        botId: {
            value: null,
            title: 'bots.filters.botId',
            allowSaveState: false,
        },
        botToolUserId: {
            value: null,
            title: 'bets.player',
            allowSaveState: true,
        },
        botEventPeriodFilter: {
            value: null,
            title: 'bots.filters.market',
            allowSaveState: true,
        },
        userPlatformId: {
            value: null,
            title: 'bets.platform',
            allowSaveState: true,
        },
        userAgentId: {
            value: null,
            title: 'bets.agent',
            allowSaveState: true,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 100,
        page: 1,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        filters.botStatus.value = null;
        filters.botName.value = null;
        filters.botId.value = null;
        filters.botToolUserId.value = null;
        filters.botEventPeriodFilter.value = null;
        filters.userPlatformId.value = null;
        filters.userAgentId.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createBotsPageHeaders());

    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title) && !header.hidden)
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'bots',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const params = {
                filters: cloneDeep(filters),
                options: {
                    sortBy: options.value.sortBy,
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['Agent', 'AuditTable'],
            };

            const response = await botService.list(params);

            items.value = (response.data.rows || []).map((row) => new BotItem(row));
            total.value = response.data.total ?? 0;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        updateOptions,
        clearData,
    };
}

export function useBotStore(storeId: string) {
    const useStore = defineStore(storeId, () => createBotStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'bots'),
    });
    return useStore();
}
